import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { usePublicShare } from '@/api/hooks/lists';
import type { PublicShareConditionOut, PublicSharedStrategyOut } from '@/api/schema';
import { ApiError, messageOf } from '@/api/errors';
import { useSession } from '@/auth/session';
import { Banner, Button, DirectionPill, Field, Icon, LoadingArea, PasswordInput, TextInput } from '@/components/ui';
import { SignInPage } from '@/features/auth/SignInPage';
import { AppleLogo } from '@/features/auth/AppleLogo';
import { authMessage } from '@/features/auth/authMessage';
import signIn from '@/features/auth/SignInPage.module.css';
import { useMediaQuery } from '@/lib/hooks';
import { rememberShare } from '@/lib/pendingShare';
import { useResolvedTheme, useThemeStore } from '@/theme/theme';
import { cx } from '@/lib/cx';
import googleG from '@/assets/google_g_logo.png';
import logoDark from '@/assets/text_logo_dark.png';
import logoLight from '@/assets/text_logo_light.png';
import s from './ShareInvitePage.module.css';

/**
 * A share link opened by someone without an account (or with one that still owes its email
 * verification): what was shared, as far as the public preview tells — name, direction, the
 * sharer's first name, each condition's indicator and timeframe, never the settings — beside a
 * sign-up panel. The code is remembered (`lib/pendingShare.ts`) the moment the preview proves the
 * link is live, so the strategy is saved once they are in, however sign-up goes.
 *
 * Signing in leaves the URL alone: the root layout then shows the signed-in `/s/<code>` route,
 * onboarding first for a new account (which builds its first strategy from this share when the
 * plan covers it).
 */
export function ShareInvitePage({ code, verify = false }: { code: string; verify?: boolean }) {
  const preview = usePublicShare(code);
  const [signIn, setSignIn] = useState(false);
  const mobile = useMediaQuery('(max-width: 639px)');

  useEffect(() => {
    if (preview.data) rememberShare(preview.data.code);
  }, [preview.data]);

  if (signIn && !verify) {
    return (
      <>
        <SignInPage />
        <button type="button" className={s.backToShare} onClick={() => setSignIn(false)}>
          <Icon name="arrow_back" size={16} /> Back to the shared strategy
        </button>
      </>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.glow} aria-hidden />
      <Nav onSignIn={verify ? undefined : () => setSignIn(true)} />
      {preview.error ? (
        <Unavailable error={preview.error} onRetry={() => void preview.refetch()} />
      ) : !preview.data ? (
        <LoadingArea />
      ) : mobile ? (
        <MobileLayout share={preview.data} verify={verify} onSignIn={() => setSignIn(true)} />
      ) : (
        <DesktopLayout share={preview.data} verify={verify} onSignIn={() => setSignIn(true)} />
      )}
    </div>
  );
}

function Nav({ onSignIn }: { onSignIn?: () => void }) {
  const theme = useResolvedTheme();
  const setPreference = useThemeStore((st) => st.setPreference);
  const next = theme === 'light' ? 'dark' : 'light';
  return (
    <nav className={s.nav}>
      <a href="https://sixone8.app" className={s.logoLink}>
        <img src={theme === 'light' ? logoLight : logoDark} alt="SixOne8" className={s.logo} />
      </a>
      <div className={s.navEnd}>
        <button type="button" className={s.themeToggle} title={`${next === 'light' ? 'Light' : 'Dark'} theme`} onClick={() => setPreference(next)}>
          <Icon name={theme === 'light' ? 'dark_mode' : 'light_mode'} size={19} />
        </button>
        {onSignIn && (
          <button type="button" className={s.navLink} onClick={onSignIn}>
            Sign in
          </button>
        )}
      </div>
    </nav>
  );
}

// --- the two layouts ------------------------------------------------------------------------------

type LayoutProps = { share: PublicSharedStrategyOut; verify: boolean; onSignIn: () => void };

function DesktopLayout({ share, verify, onSignIn }: LayoutProps) {
  const timeframes = [...new Set(share.conditions.map((c) => c.timeframe).filter(Boolean))];
  return (
    <main className={s.desktop}>
      <section className={s.story}>
        <Sharer share={share} />
        <h1 className={s.headline}>{share.name}</h1>
        <p className={s.lead}>
          A {share.condition_count}-condition {share.direction.toLowerCase()} strategy. Create a free account to see its full
          rules, test it on past market data and let SixOne8 watch the markets for it.
        </p>
        <div className={s.card}>
          <div className={s.cardHead}>
            <Icon name="tune" size={20} className={s.brandMuted} />
            <span className={s.cardName}>{share.name}</span>
            <DirectionPill direction={share.direction} size="xs" />
            <span className={s.cardTimeframes}>{timeframes.join(' · ')}</span>
          </div>
          {share.conditions.map((c, i) => (
            <ConditionRow key={i} condition={c} />
          ))}
          <div className={s.cardFoot}>
            <Icon name="visibility_lock" size={16} />
            Settings, weights and exits open once you have an account.
          </div>
        </div>
        <div className={s.benefits}>
          {BENEFITS.map((b) => (
            <div key={b.title} className={s.benefit}>
              <Icon name={b.icon} size={22} style={{ color: b.colour }} />
              <span className={s.benefitTitle}>{b.title}</span>
              <span className={s.benefitBody}>{b.body}</span>
            </div>
          ))}
        </div>
      </section>
      <aside className={s.panelFrame}>
        <div className={s.panel}>
          {verify ? (
            <VerifyPanel share={share} />
          ) : (
            <SignUpPanel
              share={share}
              title="Sign up to open the strategy you were sent"
              subtitle="It will be waiting in your Strategies, ready to test."
              onSignIn={onSignIn}
            />
          )}
          <Legal />
        </div>
      </aside>
    </main>
  );
}

function MobileLayout({ share, verify, onSignIn }: LayoutProps) {
  const [open, setOpen] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [formVisible, setFormVisible] = useState(true);
  useEffect(() => {
    const el = formRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setFormVisible(Boolean(entry?.isIntersecting)), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const chips = share.conditions.slice(0, 3);
  const more = share.conditions.length - chips.length;

  return (
    <main className={s.mobile}>
      <Sharer share={share} compact />
      <div className={s.mobileCard}>
        <div className={s.mobileCardHead}>
          <span className={s.mobileName}>{share.name}</span>
          <DirectionPill direction={share.direction} size="xs" />
        </div>
        <div className={s.chips}>
          {chips.map((c, i) => {
            const look = categoryLook(c.category);
            return (
              <span key={i} className={s.chip} style={{ color: look.colour, background: `color-mix(in srgb, ${look.colour} 16%, transparent)` }}>
                <Icon name={look.icon} size={14} />
                {c.indicator}
                {c.timeframe && ` · ${c.timeframe}`}
              </span>
            );
          })}
          {more > 0 && (
            <span className={cx(s.chip, s.chipMore)}>
              <Icon name="add" size={14} />
              {more}
            </span>
          )}
        </div>
        {open && (
          <div className={s.mobileRows}>
            {share.conditions.map((c, i) => (
              <ConditionRow key={i} condition={c} dense />
            ))}
          </div>
        )}
        <button type="button" className={s.fold} onClick={() => setOpen(!open)}>
          {open ? 'Hide conditions' : `See all ${share.condition_count} conditions`}
          <Icon name={open ? 'expand_less' : 'expand_more'} size={18} />
        </button>
      </div>

      <div ref={formRef} className={s.panelFrame}>
        <div className={cx(s.panel, s.panelCompact)}>
          {verify ? (
            <VerifyPanel share={share} />
          ) : (
            <SignUpPanel share={share} title="Open it free" subtitle="Sign up to see every rule and test it. It will be waiting in your Strategies." onSignIn={onSignIn} compact />
          )}
        </div>
      </div>

      <div className={s.mobileBenefits}>
        {BENEFITS.map((b) => (
          <div key={b.title} className={s.mobileBenefit}>
            <Icon name={b.icon} size={20} style={{ color: b.colour }} />
            <span>
              <span className={s.benefitTitle}>{b.title}</span>
              <span className={s.benefitBody}>{b.body}</span>
            </span>
          </div>
        ))}
      </div>
      <Legal />

      {!formVisible && !verify && (
        <div className={s.stickyBar}>
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          >
            Open {share.name} free
            <Icon name="arrow_forward" size={20} />
          </Button>
        </div>
      )}
    </main>
  );
}

// --- pieces --------------------------------------------------------------------------------------

const BENEFITS = [
  { icon: 'visibility', colour: 'var(--c-ai-blue)', title: 'See every rule', body: 'The settings, weights and timeframes behind each condition.' },
  { icon: 'science', colour: 'var(--c-ai-violet)', title: 'Test it', body: 'Backtest it on past market data before you rely on it.' },
  { icon: 'radar', colour: 'var(--c-ai-teal)', title: 'Watch for it', body: 'Get an alert when its conditions line up on the markets you pick.' },
];

/** An icon and colour per indicator category, so the rows read at a glance without settings. */
const CATEGORY_LOOK: Record<string, { icon: string; colour: string }> = {
  trend: { icon: 'trending_up', colour: '#5B95FA' },
  momentum: { icon: 'speed', colour: '#A78BFA' },
  volatility: { icon: 'height', colour: '#F59E0B' },
  volume: { icon: 'equalizer', colour: '#2DD4BF' },
  support_resistance: { icon: 'vertical_align_center', colour: '#F472B6' },
  price_action: { icon: 'candlestick_chart', colour: '#34D399' },
  patterns: { icon: 'pattern', colour: '#FB923C' },
  price: { icon: 'show_chart', colour: '#94A3B8' },
};

function categoryLook(category: string) {
  return CATEGORY_LOOK[category] ?? { icon: 'tune', colour: '#94A3B8' };
}

function ConditionRow({ condition: c, dense }: { condition: PublicShareConditionOut; dense?: boolean }) {
  const look = categoryLook(c.category);
  return (
    <div className={cx(s.row, dense && s.rowDense)}>
      {!dense && (
        <span className={s.rowIcon} style={{ color: look.colour, background: `color-mix(in srgb, ${look.colour} 16%, transparent)` }}>
          <Icon name={look.icon} size={17} />
        </span>
      )}
      <span className={s.rowText}>
        <span className={s.rowIndicator}>{c.indicator}</span>
        <span className={s.hidden} title="Shown after you sign up">
          <Icon name="lock" size={12} />
          ••
        </span>
      </span>
      {c.timeframe && <span className={s.timeframe}>{c.timeframe}</span>}
    </div>
  );
}

function Sharer({ share, compact }: { share: PublicSharedStrategyOut; compact?: boolean }) {
  const name = share.sharer_first_name;
  return (
    <div className={cx(s.sharer, compact && s.sharerCompact)}>
      <span className={s.avatar}>{name ? name[0]!.toUpperCase() : <Icon name="person" size={compact ? 18 : 22} />}</span>
      <span>
        {name ? <b>{name}</b> : 'Someone'} {compact ? 'sent you a strategy' : 'shared a strategy with you'}
      </span>
    </div>
  );
}

function SignUpPanel({
  share,
  title,
  subtitle,
  onSignIn,
  compact,
}: {
  share: PublicSharedStrategyOut;
  title: string;
  subtitle: string;
  onSignIn: () => void;
  compact?: boolean;
}) {
  const { devMode, retry } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const attempt = useRef(0);

  // As on the sign-in page: only the latest attempt may hand the buttons back or set the error.
  const run = async (fn: () => Promise<unknown>) => {
    const id = ++attempt.current;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      if (attempt.current === id) setError(authMessage(e));
    } finally {
      if (attempt.current === id) setBusy(false);
    }
  };
  const fb = () => import('@/auth/firebase');

  return (
    <>
      <div className={s.panelHead}>
        <span className={s.panelTitle}>{title}</span>
        <span className={s.panelSub}>{subtitle}</span>
      </div>
      {!compact && (
        <div className={s.pinned}>
          <Icon name="tune" size={18} className={s.brandMuted} />
          <span className={s.pinnedName}>{share.name}</span>
          {share.sharer_first_name && <span className={s.pinnedFrom}>from {share.sharer_first_name}</span>}
        </div>
      )}
      {error && <Banner tone="neg">{error}</Banner>}
      {devMode ? (
        // Local development signs in with the dev token, not Firebase (SignInPage says the same).
        <Button variant="primary" size="lg" block icon="login" onClick={() => void run(retry)}>
          Sign in as the dev user
        </Button>
      ) : (
        <>
          <button type="button" className={signIn.provider} disabled={busy} onClick={() => void run(async () => (await fb()).signInWithGoogle())}>
            <img src={googleG} alt="" width={18} height={18} />
            Continue with Google
          </button>
          <button type="button" className={signIn.provider} disabled={busy} onClick={() => void run(async () => (await fb()).signInWithApple())}>
            <AppleLogo />
            Continue with Apple
          </button>
          <div className={s.or}>or with email</div>
          <form
            className={s.form}
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => (await fb()).createEmailAccount(email, password));
            }}
          >
            <Field label="Email">
              {(id) => <TextInput id={id} type="email" autoComplete="email" inputMode="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />}
            </Field>
            <Field label="Password" hint="At least 8 characters.">
              {(id) => <PasswordInput id={id} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />}
            </Field>
            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              Create free account
              <Icon name="arrow_forward" size={18} />
            </Button>
          </form>
        </>
      )}
      <span className={s.panelNote}>
        Free plan · no card needed ·{' '}
        <button type="button" className={s.inlineLink} onClick={onSignIn}>
          {compact ? 'I have an account' : 'Already have an account? Sign in'}
        </button>
      </span>
    </>
  );
}

/** After an email sign-up: the share-aware form of the root layout's verification step. */
function VerifyPanel({ share }: { share: PublicSharedStrategyOut }) {
  const { confirmEmailVerified, signOut } = useSession();
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState<'confirm' | 'resend' | null>(null);
  const [message, setMessage] = useState<{ tone: 'neg' | 'pos' | 'info'; text: string } | null>(null);
  useEffect(() => {
    void import('@/auth/firebase').then((f) => setEmail(f.currentEmail()));
  }, []);
  const run = async (kind: 'confirm' | 'resend', fn: () => Promise<void>) => {
    setBusy(kind);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ tone: 'neg', text: messageOf(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <span className={s.verifyMark}>
        <Icon name="mark_email_unread" size={28} />
      </span>
      <div className={s.panelHead}>
        <span className={s.panelTitle}>Check your email</span>
        <span className={s.panelSub}>
          We sent a link to <b>{email ?? 'your email address'}</b>. Open it on this device or any other and <b>{share.name}</b> opens
          straight after.
        </span>
      </div>
      <div className={s.savedNote}>
        <Icon name="bookmark_added" size={18} />
        <span>The strategy is remembered in this browser, and the email's link leads back to it, so nothing is lost on the way through your inbox.</span>
      </div>
      {message && <Banner tone={message.tone}>{message.text}</Banner>}
      <Button
        variant="primary"
        size="lg"
        block
        loading={busy === 'confirm'}
        disabled={busy !== null}
        onClick={() =>
          void run('confirm', async () => {
            if (!(await confirmEmailVerified())) {
              setMessage({ tone: 'info', text: "That address isn't confirmed yet. Open the link in the email, then try again." });
            }
          })
        }
      >
        I&apos;ve confirmed it
      </Button>
      <div className={s.verifyActions}>
        <Button
          size="sm"
          loading={busy === 'resend'}
          disabled={busy !== null}
          onClick={() =>
            void run('resend', async () => {
              await (await import('@/auth/firebase')).resendEmailVerification();
              setMessage({ tone: 'pos', text: 'Sent. It can take a minute to arrive — check spam too.' });
            })
          }
        >
          Resend email
        </Button>
        <Button size="sm" variant="ghost" onClick={() => void signOut()}>
          Use a different email
        </Button>
      </div>
    </>
  );
}

function Legal() {
  return (
    <span className={s.legal}>
      By continuing you agree to the{' '}
      <a href="https://sixone8.app/terms" target="_blank" rel="noopener noreferrer">
        Terms
      </a>{' '}
      and{' '}
      <a href="https://sixone8.app/privacy" target="_blank" rel="noopener noreferrer">
        Privacy Policy
      </a>
      . SixOne8 is a monitoring and testing tool and does not provide investment advice. A shared strategy is someone else&apos;s
      rules, not a recommendation.
    </span>
  );
}

/** A link that never existed, was withdrawn, or is being opened too often: each with a way on. */
function Unavailable({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const navigate = useNavigate();
  const status = error instanceof ApiError ? error.status : 0;
  const copy: { title: string; body: ReactNode; retry?: boolean } =
    status === 410
      ? {
          title: 'This strategy is no longer shared',
          body: 'The person who shared it has turned the link off. You can still try SixOne8 free and build a strategy of your own.',
        }
      : status === 404
        ? {
            title: "This link doesn't work",
            body: 'It may have been copied incompletely. Ask whoever sent it to share the strategy again, or start with a free account and build your own.',
          }
        : status === 429 || status === 503
          ? {
              title: 'Too many requests',
              body: 'This preview has been opened a lot in the last minute. Wait a moment and try again, or sign in to open it directly.',
              retry: true,
            }
          : { title: "Couldn't open this link", body: messageOf(error), retry: true };
  return (
    <main className={s.unavailable}>
      <span className={s.unavailableMark}>
        <Icon name="link_off" size={32} />
      </span>
      <h1 className={s.unavailableTitle}>{copy.title}</h1>
      <p className={s.unavailableBody}>{copy.body}</p>
      <div className={s.unavailableActions}>
        {copy.retry && (
          <Button size="lg" icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        )}
        {/* Off the dead link before signing in, so the account does not open on it. */}
        <Button variant="primary" size="lg" onClick={() => void navigate({ to: '/today' })}>
          Try SixOne8 free
        </Button>
        <a className={s.ghostLink} href="https://sixone8.app">
          See what it does
        </a>
      </div>
    </main>
  );
}
